import {
  ASK_PROFILE_PROMPTS,
  ASK_PROFILES,
  DEFAULT_ASK_PROFILE,
  DEFAULT_EXTRA_SYSTEM_PROMPT,
  USER_EXTRA_PROMPT,
  type AnswerLanguage,
  type AskProfile,
  type PromptProfile,
} from "../api/prompt";
import { getSetting, setSetting } from "../db/settings";

// Bump when introducing a migration in `loadConfig`. v1 = initial seeding of
// `extraSystemPrompt` with DPO domain content (previously hardcoded in the
// system prompts). v2 = `testSuite` field added, seeded with the standard DPO
// battery of questions for users who don't already have one. v3 = chat model
// narrowed to two presets (LARGE/SMALL) and `rewriteModel` hardcoded — old
// free-form values are migrated to LARGE_MODEL on load.
// v4 = optional EPFL hybrid search (`epflHybridEnabled` / `epflHybridTopK`),
// seeded OFF so behaviour is unchanged until the user opts in. v5 = optional
// Exa web search (`exaWebEnabled` / `exaWebTopK`), also seeded OFF. v6 = optional
// agentic multi-pass search (`agenticEnabled` / `agenticMaxPasses`), seeded OFF.
// v7 = mode Ultra (`ultraEnabled` / `ultraBackendUrl`) : le chat est routé vers
// le backend agent (harness Claude Code + GLM-5.2 via RCP) au lieu de
// l'orchestration locale ; les tools search_local/search_web restent exécutés
// dans l'extension via un aller-retour SSE. Seeded OFF.
// v8 = choix du harness Ultra (`ultraHarness`) : "claude-code" (propriétaire)
// ou "openhands" (MIT) — mêmes tools, pour comparer les moteurs. Retiré le
// 24.09.2026 : le backend tourne sur OpenHands pour tout le monde.
// v9 = les trois compteurs de sources (chunks locaux, sources EPFL, resultats
// web) ne sont plus reglables : trancher une fois vaut mieux que faire
// arbitrer chaque usager sur un parametre dont il n'a aucun moyen de mesurer
// l'effet. `epflHybridTopK` passe de 8 a 10, la valeur retenue par le banc
// ServiceNow du 03.08.2026 (epfl-scraper/docs/rapport-benchmarks-2026-08.md) :
// au-dela, aucun gain mesurable et 2,6x les jetons. Les valeurs stockees sont
// ramenees aux constantes a chaque chargement.
// v10 = catalogue ramené à DEUX modèles — Qwen3.6-35B par défaut, Kimi K2.7 en
// escalade — au terme de l'évaluation de bout en bout du 10.08.2026
// (epfl-scraper, docs/rapport-evaluation-assistants-2026-08.md). gpt-oss et
// Mistral Small sont retirés ; toute valeur stockée pointant sur eux est
// remappée sur le défaut au chargement.
// v11 = GLM-5.3-Flash remplace Qwen3.6-35B en Standard (22.09.2026), au terme
// de la campagne DPO-Agent/docs/loadtest/resultats-2026-09-15.md. Le contrôle
// « le modèle stocké appartient-il au catalogue » tourne à CHAQUE chargement et
// non à la migration : les configurations pointant sur Qwen3.6 basculent donc
// seules. Ce numéro documente la génération du catalogue, il ne déclenche rien.
// v12 = le choix Standard/Large (`chatProfile`) devient un interrupteur
// Réflexion on/off (`thinkingEnabled`), exposé aussi en bulle dans la barre du
// chat (24.09.2026) : les deux profils ne différaient déjà plus que par là.
// Large → réflexion activée, Standard → coupée.
// v13 = deux modes seulement (24.09.2026). Réflexion coupée : recherche fixe,
// rapide. Réflexion activée : l'agent (backend OpenHands) mène ses recherches
// et réfléchit. Le mode Ultra (`ultraEnabled`) et la recherche itérative
// locale (`agenticEnabled` / `agenticMaxPasses`) disparaissent : qui avait
// activé Ultra garde l'agent, donc la réflexion.
// v14 = réglages de l'interface v3 (13.10.2026) : grilles de questions nommées
// (`grids`, la première reprend `testSuite`), langue des réponses
// (`answerLanguage`) et modèle suisse Apertus en option (`useApertus`).
// v15 = bulle « LEX » (19.10.2026) : recherche EPFL limitée aux textes de
// Polylex. Cachée par défaut (`lexButtonVisible`), pour ne pas encombrer la
// barre de tout le monde ; s'affiche depuis les réglages. Pas de migration :
// les deux champs naissent à false par la fusion avec DEFAULT_CONFIG.
// v16 = PolyChat (20.10.2026) : profil parmi quatre (`askProfile`) et consigne
// personnelle en langage courant (`personalInstruction`, vide par défaut),
// lus par la v3 seulement ; la v1 garde `promptProfile` et `extraSystemPrompt`.
// v17 = consigne éditable (23.10.2026) : choisir un profil recopie son texte
// dans `personalInstruction`, qui le remplace dans le prompt. Une consigne
// d'avant s'ajoutait au profil : on la garde derrière le texte du profil.
// v18 = liste « Note de frais » (Figma 23-A, 31.10.2026) :
// la liste de questions par défaut pour le personnel administratif, ajoutée
// une fois en tête des listes ; « Analyse LPD » reste proposée.
const CURRENT_CONFIG_VERSION = 18;

// UN SEUL modèle de chat, et un interrupteur Réflexion qui règle la quantité de
// réflexion qu'on lui accorde. Toute autre valeur stockée dans `rcpModel` est
// ramenée à `CHAT_MODEL` au chargement.
//
//   Réflexion coupée    réflexion réduite (`reasoning_effort: low`), défaut
//   Réflexion activée   réflexion libre — +0,19 point en recherche, rien de
//                       plus en lecture de document, pour sept fois plus de
//                       jetons
//
// Kimi K2.7 tenait le rôle de Large jusqu'au 22.09.2026. GLM-5.3-Flash le bat
// en lecture de document (2,99 contre 2,79 ; 1 invention sur 108 contre 4) et
// lui offre quatre fois son contexte (1 048 576 jetons contre 256 000), ce qui
// vidait de son sens le « pour les très gros volumes » qui le justifiait.
// ⚠️ Kimi n'a PAS été mesuré sur le banc de recherche dans cette configuration
// et avec ce juge : la bascule se fonde sur les documents et le contexte, pas
// sur la recherche.
//
// GLM-5.3-Flash remplace Qwen3.6-35B-A3B, qui tenait le rôle de Standard depuis
// le 10.08. Mesures : DPO-Agent/docs/loadtest/resultats-2026-09-15.md.
//
//   lecture de document  2,99 contre 2,98 — jeu égal (écarts sous les IC),
//                        1 invention sur 108 comme lui, et 0 sur les deux
//                        questions de droit suisse où les documents se taisent,
//                        contre 1 ; 12 schémas mermaid valides sur 12.
//   recherche            +0,27 point en apparié, ticket par ticket, juge
//                        Kimi-K2.7 — significatif (IC ±0,10).
//   vitesse              23 s de médiane au banc documents contre 54 s.
//   contexte             1 048 576 jetons, mesuré, contre 262 144.
//
// ⚠️ LE RAISONNEMENT SE COUPE AUTREMENT QUE CHEZ QWEN, et se tromper ne lève
// aucune erreur : `enable_thinking: false` ne le désactive pas, il le déverse
// dans `content` À LA PLACE de la réponse. Le seul levier est
// `reasoning_effort: "low"`. Tout passe donc par `reasoningParams()`
// (api/rcpClient.ts) — ne pas réintroduire de `chat_template_kwargs` en dur.
//
// ⚠️ `low` N'EST PAS GRATUIT PARTOUT, et c'est la raison d'être des deux
// profils. Sur la lecture de document il ne coûte rien (2,99 dans les deux
// modes, 7 fois moins de jetons) ; sur la RECHERCHE il coûte 0,19 point en
// apparié — réfléchir sert quand il faut trier des passages.
//
// /!\ Qwen3.8-27B reste écarté. Meilleur du panel en lecture de document
// (3,00 ; 0 invention sur 108), il s'effondre en charge : 30 jetons/s à 50
// usagers et 8 minutes de TTFT p95, son architecture étant dense (27 milliards
// de paramètres actifs par jeton contre 3 à Qwen3.6-35B-A3B).
export const CHAT_MODEL = "zai-org/GLM-5.3-Flash";

// Modèle suisse proposé en option dans les réglages v3 (« Modèle d'IA »,
// interrupteur). Disponible sur RCP (vérifié le 13.10.2026 : réponse en ~2 s,
// contexte 262 144 jetons). ⚠️ PAS ENCORE PASSÉ AU BANC QUALITÉ : l'interface
// annonce « réponses parfois moins précises ». Il ne sert qu'à la réponse
// finale en mode Rapide ; la réécriture de requête reste sur CHAT_MODEL et la
// Réflexion passe par l'agent, qui a son propre modèle.
export const APERTUS_MODEL = "swiss-ai/Apertus-v1.5-70B";

/** Recherche « LEX seulement » en vigueur : bulle affichée ET enfoncée. Cacher
 *  la bulle depuis les réglages coupe donc aussi le mode, sans qu'il reste
 *  actif à l'insu de l'usager. */
export function lexOnlyActive(cfg: Pick<Config, "lexButtonVisible" | "lexOnlyEnabled">): boolean {
  return !!cfg.lexButtonVisible && !!cfg.lexOnlyEnabled;
}

/** Le modèle qui rédige la réponse finale en mode Rapide. */
export function answerModel(cfg: Pick<Config, "rcpModel" | "useApertus">): string {
  return cfg.useApertus ? APERTUS_MODEL : cfg.rcpModel;
}

/** Budget de lecture intégrale, en jetons (PolyChat, 21.10.2026) : les
 *  documents interrogés sont envoyés en entier tant que leur total tient
 *  dedans ; au-delà, seuls les passages retrouvés par la recherche partent.
 *  80 % du seuil « lent » du modèle : 200 000 jetons pour GLM-5.3-Flash
 *  (~17 s de prefill), 88 000 pour Apertus. Même budget pour la révision
 *  d'un Word, qui exige le document entier. */
export function fullTextBudget(cfg: Pick<Config, "rcpModel" | "useApertus">): number {
  return Math.round(tokenLimitsFor(answerModel(cfg)).soft * 0.8);
}

/** Ces documents (leur texte, en caractères) tiennent-ils dans le budget ? */
export function docsFitFullText(cfg: Pick<Config, "rcpModel" | "useApertus">, chars: number): boolean {
  return chars / 4 <= fullTextBudget(cfg);
}

// Rétrocompatibilité : du code importe encore ces noms. Ils désignent tous le
// modèle unique. À supprimer une fois les appelants migrés.
export const STANDARD_MODEL = CHAT_MODEL;
export const MEDIUM_MODEL = CHAT_MODEL;
export const SMALL_MODEL = CHAT_MODEL;

// Le modèle qui RÉÉCRIT les requêtes n'est pas configurable. Il suivait Mistral
// Small ; il suit désormais le modèle standard, qui est à la fois meilleur et
// plus rapide (3 milliards de paramètres activés par jeton). Forcé à chaque
// chargement pour qu'une valeur périmée ne casse pas la récupération.
export const REWRITE_MODEL = STANDARD_MODEL;

// Seuils d'alerte de volume en mode texte intégral. `soft` déclenche un
// avertissement « lent / risque d'échec », `hard` signale que le volume dépasse
// probablement la fenêtre de contexte.
//
// Le plafond `hard` est MESURÉ, pas repris d'une fiche modèle : une requête
// volontairement démesurée fait dire au serveur « maximum context length is N »
// (22.09.2026). GLM-5.3-Flash : 1 048 576. Pour mémoire, Qwen3.6-35B en sert
// 262 144 — le double des 128 000 que ce code supposait, l'alerte était deux
// fois trop prudente ; Kimi K2.7, 256 000.
//
// Le seuil `soft` ne suit PAS la fenêtre : à un million de jetons, le seul
// prefill prend ~87 s au débit mesuré (~12 000 jetons/s), avant même la
// rédaction. Ce n'est pas la fenêtre qui gêne l'usager à ce volume, c'est
// l'attente — d'où un avertissement dès 250 000, où le prefill atteint ~20 s.
//
// Les deux profils partagent le modèle, donc la même fenêtre : l'argument
// « passer en Large pour les gros volumes » n'a plus lieu d'être.
export function tokenLimitsFor(model: string): { soft: number; hard: number } {
  if (model === CHAT_MODEL) return { soft: 250_000, hard: 1_048_576 };
  if (model === APERTUS_MODEL) return { soft: 110_000, hard: 262_144 };
  return { soft: 110_000, hard: 128_000 };
}

// Standard battery of questions the EPFL DPO runs against every project. Used
// as the default `testSuite` for new users and seeded on v1 → v2 migration
// when the user has no prior list. Users can edit / reorder / remove freely
// in the Configuration panel.
export const DEFAULT_TEST_SUITE: string[] = [
  "Quelles données sont traitées ?",
  "Y a-t-il des données sensibles ?",
  "Quel rôle a l'EPFL sur ce projet du point de vue de la LPD ? Quel rôle a l'EPFL selon la LRH ?",
  "Quels sont les rôles des autres entités sur ce projet ? Il faudrait avoir les rôles relativement à la LPD et à la LRH",
  "Qui demande le consentement des patients ?",
  "Où les données seront stockées ? plus généralement, où les données seront traitées ?",
  "Est-ce que des fournisseurs étrangers sont concernés ?",
  "Quelles mesures de protection a-t-on envisagé de mettre en place pour protéger les données ?",
  "Peux-tu réaliser un schéma présentant le flux des données concernées par ce projet ?",
];

/** Une grille de questions nommée (« Analyse LPD », « Contrat de recherche »…). */
export interface QuestionGrid {
  id: string;
  name: string;
  questions: string[];
}

/** La grille livrée par défaut, qui reprend `testSuite` (v1 l'édite encore). */
export const DEFAULT_GRID_ID = "default";
export const DEFAULT_GRID_NAME = "Analyse LPD";

/** La liste par défaut de la v3 (Figma 23-A) : le contrôle d'une note de
 *  frais. Distincte de DEFAULT_GRID_ID, que la v1 édite comme `testSuite`. */
export const EXPENSE_GRID_ID = "expense";
export const EXPENSE_GRID_NAME = "Note de frais";
export const EXPENSE_QUESTIONS: string[] = [
  "Le justificatif est-il daté et au nom de la personne ?",
  "Les montants respectent-ils les barèmes en vigueur ?",
  "La demande a-t-elle été soumise dans le délai prévu ?",
  "Les fonds imputés sont-ils indiqués ?",
  "La dépense est-elle attribuée à la bonne catégorie ?",
  "Les pièces justificatives manquantes sont-elles signalées ?",
];

export interface Config {
  serverUrl: string;
  rcpUrl: string;
  rcpModel: string;
  /** Réflexion libre (true) ou réduite (false) pour la RÉPONSE FINALE. Les
   *  passes mécaniques (réécriture, tri, choix d'outil) restent en réflexion
   *  coupée dans tous les cas : c'est gratuit en qualité et ça évite les JSON
   *  vides. Remplace `chatProfile` ("large" = true) depuis la v12. */
  thinkingEnabled: boolean;
  rewriteModel: string;
  // Must match the model the server uses at ingestion (otherwise query and
  // chunk vectors live in different spaces and retrieval breaks). Not exposed
  // in the UI for that reason.
  embeddingModel: string;
  // Which prompt preset is currently selected. The radio in ConfigView writes
  // the matching preset into `extraSystemPrompt` when changed; the user may
  // then edit `extraSystemPrompt` freely (the profile label remains, but the
  // UI flags the divergence with a "(modifié)" hint).
  promptProfile: PromptProfile;
  // Free-form text appended to the system prompt (chat libre and RAG modes).
  // Pre-seeded from the selected promptProfile; user can edit or clear it
  // from the Configuration tab without touching the preset selector.
  extraSystemPrompt: string;
  // PolyChat (v3) : profil choisi, qui n'est pas modifiable, et préférences de
  // l'usager ajoutées après lui. Le socle commun (rôle, citations, garde-fous)
  // reste hors de portée de l'usager.
  askProfile: AskProfile;
  personalInstruction: string;
  // Top-K du retrieval cosinus initial. FIGE a 10 depuis la v9 : plus expose
  // dans la configuration. Conserve dans le type parce que le champ existe
  // dans les configurations deja enregistrees.
  topKInitial: number;
  // "none" = no data-flow-specific instructions injected (default).
  // "simple" = plain Mermaid flowchart with nodes + arrows.
  // "complete" = each canonical stage (sources, collecte, pseudonymisation, ...) is wrapped
  // in a labelled `subgraph` so the diagram visually shows which stage each actor belongs to.
  dataFlowMode: "none" | "simple" | "complete";
  // Ordered list of standard questions executed sequentially by the "Run test
  // suite" button in the chat panel. Stored here so the DPO can curate a
  // stable battery of regression questions and re-run them without retyping.
  testSuite: string[];
  // Grilles nommées (v3). La grille DEFAULT_GRID_ID est le miroir de
  // `testSuite` : la v3 écrit les deux, la v1 n'écrit que `testSuite` et la
  // grille se resynchronise au chargement.
  grids: QuestionGrid[];
  // Langue imposée aux réponses ("auto" = celle de la question).
  answerLanguage: AnswerLanguage;
  // Réponse finale rédigée par Apertus au lieu de CHAT_MODEL (mode Rapide).
  useApertus: boolean;
  // When true, every RAG turn also queries the EPFL hybrid endpoint
  // (site + ServiceNow KB + résolus incidents, OBO) and merges those hits into
  // the local retrieval. Opt-in (default false) and gated on an EPFL session.
  epflHybridEnabled: boolean;
  // Nombre de sources EPFL/ServiceNow demandees a la recherche hybride. FIGE a
  // 10 depuis la v9 : plus expose dans la configuration.
  epflHybridTopK: number;
  // Bulle « LEX » montrée dans la barre du chat (réglage, défaut false).
  lexButtonVisible: boolean;
  // Bulle « LEX » enfoncée : la recherche EPFL ne porte que sur les textes de
  // Polylex (LEX, directives, annexes, textes fédéraux), à la place du site +
  // ServiceNow. Sans effet tant que la bulle est cachée — cf. `lexOnlyActive`.
  lexOnlyEnabled: boolean;
  // When true, every turn also runs an Exa web search (hosted MCP, keyless) and
  // merges those results as cited sources. Opt-in (default false).
  exaWebEnabled: boolean;
  // Nombre de resultats web demandes a Exa. FIGE depuis la v9 : plus expose
  // dans la configuration.
  exaWebTopK: number;
  // URL du backend agent (glm-agent-server, serveur unique multi-frontends),
  // utilisé quand la réflexion est activée. Plus exposée dans l'interface et
  // ramenée au défaut à chaque chargement ; pour développer le backend en local
  // (http://localhost:8790), changer le défaut ici.
  ultraBackendUrl: string;
  // Schema version of the persisted config. Drives migrations in `loadConfig`.
  configVersion: number;
}

export const DEFAULT_CONFIG: Config = {
  serverUrl: "https://personal-rag.epfl.ch",
  rcpUrl: "https://inference.rcp.epfl.ch/v1",
  rcpModel: CHAT_MODEL,
  thinkingEnabled: false,
  rewriteModel: REWRITE_MODEL,
  embeddingModel: "Qwen/Qwen3-Embedding-8B",
  promptProfile: "user",
  extraSystemPrompt: USER_EXTRA_PROMPT,
  askProfile: DEFAULT_ASK_PROFILE,
  personalInstruction: "",
  topKInitial: 10,
  dataFlowMode: "none",
  testSuite: [...DEFAULT_TEST_SUITE],
  grids: [
    { id: EXPENSE_GRID_ID, name: EXPENSE_GRID_NAME, questions: [...EXPENSE_QUESTIONS] },
    { id: DEFAULT_GRID_ID, name: DEFAULT_GRID_NAME, questions: [...DEFAULT_TEST_SUITE] },
  ],
  answerLanguage: "auto",
  useApertus: false,
  epflHybridEnabled: false,
  epflHybridTopK: 10,
  lexButtonVisible: false,
  lexOnlyEnabled: false,
  exaWebEnabled: false,
  exaWebTopK: 5,
  ultraBackendUrl: "https://expert-finder.epfl.ch/outlook/agent",
  configVersion: CURRENT_CONFIG_VERSION,
};

// Bornes du clamp defensif applique dans chat.ts. Ce ne sont plus des bornes
// d'interface : le nombre de chunks n'est plus reglable.
export const TOP_K_MIN = 5;
export const TOP_K_MAX = 20;


const KEY = "app.config";

/** Version web : le serveur est celui qui sert la page (même origine). Il
 *  n'accepte pas d'appels d'un autre domaine, et la base locale est propre à
 *  chaque domaine : sur polychat.epfl.ch (22.10.2026), une configuration neuve
 *  aurait appelé personal-rag.epfl.ch et échoué. Vaut aussi en local
 *  (localhost:8000, 8010…). L'extension garde son réglage. */
function serverUrlFor(stored: string): string {
  const web = typeof __PLATFORM__ !== "undefined" && __PLATFORM__ === "web";
  return web && typeof location !== "undefined" ? location.origin : stored;
}

export async function loadConfig(): Promise<Config> {
  const v = await getSetting<Partial<Config>>(KEY);
  // Fresh install (no stored config): the defaults are perfectly usable on
  // their own (prod EPFL server URL, standard models). Returning null here
  // used to leak through to uploadFiles / sendUserMessage as `if (!app.config)
  // return;` — silent no-op with no UI feedback. Always returning a Config
  // means "uploaded but no edit yet" works like "logged in user who never
  // touched settings" instead of breaking.
  if (!v) return { ...DEFAULT_CONFIG, serverUrl: serverUrlFor(DEFAULT_CONFIG.serverUrl) };
  // Merge with defaults so new fields introduced after the user's last save
  // are filled in with sensible values instead of leaving them undefined.
  const merged: Config = { ...DEFAULT_CONFIG, ...v };
  const storedVersion = v.configVersion ?? 0;
  if (storedVersion < 1) {
    // v0 → v1 : the DPO domain content used to be hardcoded in the system
    // prompts; it now lives in `extraSystemPrompt`. Seed it for users who
    // never customized the field (empty or undefined). Anyone who already
    // wrote their own text keeps it untouched.
    if (!v.extraSystemPrompt) {
      merged.extraSystemPrompt = DEFAULT_EXTRA_SYSTEM_PROMPT;
    }
  }
  if (storedVersion < 2) {
    // v1 → v2 : new `testSuite` field. Seed it with the standard DPO battery
    // for users who never had a list (field absent or empty array). Anyone
    // who already curated their own list keeps it untouched.
    if (!Array.isArray(v.testSuite) || v.testSuite.length === 0) {
      merged.testSuite = [...DEFAULT_TEST_SUITE];
    }
  }
  if (storedVersion < 3) {
    // v2 → v3 : the chat model is now picked between two presets only.
    // Anything else (Qwen, Gemma, an arbitrary string the user typed in)
    // is mapped to LARGE_MODEL — that's the closest match to the previous
    // default (a large reasoning-capable model).
    // (Depuis le 22.09 les deux préréglages partagent le même modèle : cette
    // migration ancienne n'a plus d'effet, le garde-fou de catalogue plus bas
    // ramène de toute façon toute valeur sur CHAT_MODEL. Conservée telle
    // quelle pour ne pas réécrire l'historique des migrations.)
    if (![CHAT_MODEL, MEDIUM_MODEL, SMALL_MODEL].includes(merged.rcpModel)) {
      merged.rcpModel = CHAT_MODEL;
    }
  }
  if (storedVersion < 4) {
    // v3 → v4 : EPFL hybrid search introduced. Seed it OFF (opt-in) and set a
    // sensible source count for configs saved before the fields existed. The
    // `{ ...DEFAULT_CONFIG, ...v }` merge already covers absent keys; this is a
    // defensive guard against a stored value of the wrong type.
    if (typeof v.epflHybridEnabled !== "boolean") {
      merged.epflHybridEnabled = false;
    }
    if (typeof v.epflHybridTopK !== "number") {
      merged.epflHybridTopK = DEFAULT_CONFIG.epflHybridTopK;
    }
  }
  // v15 : bulle LEX. Même garde-fou que pour la v4 : une valeur absente ou du
  // mauvais type la cache et la relâche.
  if (typeof v.lexButtonVisible !== "boolean") merged.lexButtonVisible = false;
  if (typeof v.lexOnlyEnabled !== "boolean") merged.lexOnlyEnabled = false;
  if (storedVersion < 5) {
    // v4 → v5 : Exa web search introduced. Seed it OFF (opt-in).
    if (typeof v.exaWebEnabled !== "boolean") {
      merged.exaWebEnabled = false;
    }
    if (typeof v.exaWebTopK !== "number") {
      merged.exaWebTopK = DEFAULT_CONFIG.exaWebTopK;
    }
  }
  // v11 → v12 : l'interrupteur Réflexion se déduit de l'ancien profil, et
  // celui-ci de l'ancien choix de modèle AVANT que ce dernier soit écrasé : qui
  // avait choisi Large (ou Kimi K2.7, le Large d'avant le 22.09) voulait la
  // meilleure réponse — il garde donc la réflexion libre.
  if (typeof v.thinkingEnabled !== "boolean") {
    const legacy = v as { chatProfile?: string; rcpModel?: string };
    merged.thinkingEnabled =
      legacy.chatProfile === "large" ||
      (legacy.chatProfile === undefined &&
        typeof legacy.rcpModel === "string" &&
        /kimi/i.test(legacy.rcpModel));
  }
  delete (merged as { chatProfile?: unknown }).chatProfile;
  // v12 → v13 : qui avait activé Ultra voulait l'agent — il l'a désormais
  // avec la réflexion. Ultra, son harness et la recherche itérative disparaissent.
  const legacy13 = v as { ultraEnabled?: unknown };
  if (storedVersion < 13 && legacy13.ultraEnabled === true) {
    merged.thinkingEnabled = true;
  }
  for (const gone of ["ultraEnabled", "ultraHarness", "agenticEnabled", "agenticMaxPasses"]) {
    delete (merged as unknown as Record<string, unknown>)[gone];
  }
  // Même raison que pour les compteurs plus bas : un réglage sans interface ne
  // doit pas survivre (une URL de test restée en mémoire casserait l'agent).
  merged.ultraBackendUrl = DEFAULT_CONFIG.ultraBackendUrl;
  // Le modèle de chat doit appartenir au catalogue courant — vérifié à CHAQUE
  // chargement, pas seulement à la migration. gpt-oss et Mistral Small ont été
  // retirés pour mauvais résultats ; Kimi K2.7 l'a été le 22.09 (battu en
  // lecture de document par GLM-5.3-Flash, 2,79 contre 2,99, et quatre fois
  // moins de contexte). Une valeur hors catalogue bascule sur le modèle unique.
  if (merged.rcpModel !== CHAT_MODEL) {
    merged.rcpModel = CHAT_MODEL;
  }
  // v14 : la grille par défaut suit `testSuite`, que la v1 continue d'éditer.
  // Contrôlé à chaque chargement, pas seulement à la migration.
  const grids = Array.isArray(v.grids) ? v.grids.filter((g) => g && typeof g.id === "string") : [];
  // v18 : la liste des notes de frais, ajoutée une fois ; supprimée ensuite,
  // elle ne revient pas.
  if (storedVersion < 18 && !grids.some((g) => g.id === EXPENSE_GRID_ID)) {
    grids.unshift({ id: EXPENSE_GRID_ID, name: EXPENSE_GRID_NAME, questions: [...EXPENSE_QUESTIONS] });
  }
  const expense = grids.filter((g) => g.id === EXPENSE_GRID_ID);
  const others = grids.filter((g) => g.id !== DEFAULT_GRID_ID && g.id !== EXPENSE_GRID_ID);
  const defaultName = grids.find((g) => g.id === DEFAULT_GRID_ID)?.name || DEFAULT_GRID_NAME;
  merged.grids = [
    ...expense,
    { id: DEFAULT_GRID_ID, name: defaultName, questions: [...merged.testSuite] },
    ...others,
  ];
  if (!["auto", "fr", "en"].includes(merged.answerLanguage)) merged.answerLanguage = "auto";
  if (typeof merged.useApertus !== "boolean") merged.useApertus = false;
  // Always force the rewrite model — it's no longer user-configurable.
  merged.rewriteModel = REWRITE_MODEL;
  // Idem pour le nombre de chunks récupérés et le nombre de résultats web :
  // forcés à chaque chargement, pas seulement à la migration. Une valeur
  // choisie autrefois par l'usager, ou écrite à la main dans IndexedDB, ne doit
  // pas survivre au retrait du réglage — sinon deux usagers ont deux
  // comportements sans que rien ne l'indique dans l'interface.
  merged.serverUrl = serverUrlFor(merged.serverUrl);
  merged.topKInitial = DEFAULT_CONFIG.topKInitial;
  merged.epflHybridTopK = DEFAULT_CONFIG.epflHybridTopK;
  merged.exaWebTopK = DEFAULT_CONFIG.exaWebTopK;
  // v16 : profil PolyChat inconnu ou absent → profil par défaut.
  if (!ASK_PROFILES.includes(merged.askProfile)) merged.askProfile = DEFAULT_ASK_PROFILE;
  if (typeof merged.personalInstruction !== "string") merged.personalInstruction = "";
  if (storedVersion < 17 && merged.personalInstruction.trim()) {
    merged.personalInstruction = `${ASK_PROFILE_PROMPTS[merged.askProfile]}\n\n${merged.personalInstruction.trim()}`;
  }
  merged.configVersion = CURRENT_CONFIG_VERSION;
  return merged;
}

export async function saveConfig(cfg: Config): Promise<void> {
  await setSetting(KEY, cfg);
}

export function isConfigComplete(cfg: Config | null): cfg is Config {
  // OIDC session lives outside the config object and is checked separately
  // at the call site. All LLM traffic goes through the server proxy, so no
  // personal RCP key exists anymore — only model identifiers and URLs need
  // to be filled in here.
  return (
    !!cfg &&
    !!cfg.serverUrl &&
    !!cfg.rcpUrl &&
    !!cfg.rcpModel &&
    !!cfg.rewriteModel &&
    !!cfg.embeddingModel
  );
}

/** Comptes qui voient le réglage des schémas de flux de données dans la v3 :
 *  la DPO et le mainteneur (affichage, pas une permission). */
export const DATA_FLOW_USERS = ["yannick.pezeu@epfl.ch", "chiara.tanteri@epfl.ch"];

// Portée de la recherche (v3, Figma 01c) : un seul choix parmi quatre, posé
// sur les interrupteurs que la v1 règle séparément. Les documents interrogés
// le sont toujours ; « Mes documents » coupe seulement les sources EPFL.
export type SearchScope = "docs" | "polylex" | "epfl" | "web";

export function searchScope(
  cfg: Pick<Config, "epflHybridEnabled" | "exaWebEnabled" | "lexButtonVisible" | "lexOnlyEnabled">,
): SearchScope {
  if (lexOnlyActive(cfg)) return "polylex";
  if (cfg.exaWebEnabled) return "web";
  if (cfg.epflHybridEnabled) return "epfl";
  return "docs";
}

export function scopeConfig(scope: SearchScope): Partial<Config> {
  switch (scope) {
    case "docs":
      return { epflHybridEnabled: false, lexOnlyEnabled: false, exaWebEnabled: false };
    case "polylex":
      return { epflHybridEnabled: false, lexButtonVisible: true, lexOnlyEnabled: true, exaWebEnabled: false };
    case "epfl":
      return { epflHybridEnabled: true, lexOnlyEnabled: false, exaWebEnabled: false };
    case "web":
      return { epflHybridEnabled: true, lexOnlyEnabled: false, exaWebEnabled: true };
  }
}
